import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { selectLandingType, selectPlan, selectPlanCoupon, selectShowCheckout, setDefaultPlanId } from './plansSlice';
import { selectFrequency, selectLocation } from '../track/infoSlice';
import { TimerSimple } from '../../UI/Timer';
import UIButton from '../../UI/UIButton';
import { CardIconsList } from '../checkout/CheckoutScreen';
import { Label, Rate, ReviewBody, ReviewHead, ReviewItem, ReviewText, ReviewerName, ReviewerPhoto, UserIcon } from './styled';
import { StyledLogo } from '../success/SuccessScreen';
import { Logo } from '../../UI/Logo';
import { wh } from '../../mixins';
import { PLANS } from '../../analytics/eCommerce';

const isArabic = navigator.language.startsWith('ar');

export const Wrapper = styled.div`
	padding: 24px 0;
	position: relative;
	background-color: #ffffff;
`;

const StyledLogoWrapper = styled(StyledLogo)`
  ${wh('65px')};
  margin-bottom: 16px;
`

export const TimerBlock = styled.div`
	margin-bottom: 24px;
	padding: 6px 0;
	display: flex;
	justify-content: space-between;
  align-items: center;
	position: sticky;
	top: 0;
	left: 0;
	z-index: 2;
	text-align: left;
	
	&:before {
		content: '';
		display: block;
		width: 100vw;
		height: 100%;
		background: #FFECDB;
		position: absolute;
		top: 0;
		left: calc(50% - 50vw);
		z-index: -1;
		isolation: isolate;
	}
`;

export const TimerBlockDiscount = styled.div`
	font-size: 14px;
	line-height: 17px;
	color: #0F0E0E;
	margin-bottom: 2px;
`;

export const Title = styled.div`
	font-weight: 700;
	font-size: 28px;
	color: #0F0E0E;
	text-align: center;
	white-space: pre-line;
	margin: ${({ customMargin }) => customMargin || 0};
  span {
		color: #F58A3C;
	}
`;

export const Description = styled.div`
	font-size: 16px;
  font-weight: 400;
  color: #0F0E0E;
	text-align: center;
`;

const StyledImage = styled.div`
	aspect-ratio: 327/314;
	width: 100%;
	background: url("../assets/plans/headway-paywall.png") center no-repeat;
	background-size: contain;
	margin: 32px 0 40px;
`;

const ChoicesBlock = styled.div`
	&:not(:empty) {
		margin-top: 16px;
	}
`;

const ChoicesBlockItem = styled.div`
	display: flex;
	align-items: center;
	
	&:not(:first-child) {
		margin-top: 16px;
	}
`;

const ChoicesIcon = styled.div`
	flex-shrink: 1;
	margin-right: 16px;
`;

const ChoicesText = styled.div`
	text-align: left;
	max-width: 250px;
	
	${({ isFrequency }) => isFrequency && `
		display: flex;
		flex-direction: column;	
	`}
`;

const ChoicesItems = styled.span`
	font-weight: 700;
`;

export const PlanList = styled.ul`
	display: flex;
	flex-direction: column;
	width: 100%;
	padding: 0;
	margin: 24px 0 16px 0;
	list-style: none;
`;

export const PlanLi = styled.li`
	display: flex;
	flex-direction: ${isArabic ? 'row-reverse' : 'row'};
	justify-content: space-between;
	align-items: center;
	background-color: #fff;
	border: 1px solid ${({ active }) => active ? '#F58A3C' : '#C4C4C4'};
	border-radius: 12px;
	width: 100%;
	height: 86px;
	padding: 0 16px;
	position: relative;
	cursor: pointer;

	&:not(:last-child) {
		margin-bottom: 8px;
	}
`;

export const StyledPeriod = styled.div`
	display: flex;
	flex-direction: ${isArabic ? 'row-reverse' : 'row'};
	align-items: center;
`;

export const PlanCheck = styled.div`
	--check-size: 24px;
	width: var(--check-size);
	height: var(--check-size);
	border-radius: 50%;
	border: 1px solid ${({ active }) => active ? '#F58A3C' : '#AEAEB4'};
	position: relative;
	margin-right: 16px;
	
	&:after {
		content: '';
		display: block;
		width: calc(var(--check-size)*(2/3));
		height: calc(var(--check-size)*(2/3));
		background-color: ${({ active }) => active ? '#F58A3C' : 'transparent'};
		border-radius: inherit;
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
`;

export const PlanTitle = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: flex-start;
	font-weight: 700;
	font-size: 16px;
	color: #323232;
`;

export const StyledFullPrice = styled.div`
	font-weight: 500;
	font-size: 14px;
	line-height: 17px;
	color: #AEAEB4;
	margin-top: 8px;
	
	strike {
		margin-right: 8px;
	}
`;

export const StyledPriceWrapper = styled.div`
	display: flex;
	${({ isTrial }) => !isTrial && 'align-items: center'};
`;

export const OldPrice = styled.div`
	color: #AEAEB4;
	font-weight: 500;
	font-size: 16px;
	text-decoration: line-through #FF6363;
	margin-right: 10px;
	margin-top: -4px;
`;

export const PriceWrapper = styled.div`
	display: flex;
	font-weight: 600;
	font-size: 60px;
	line-height: 46px;
	text-align: center;
	color: ${({ active }) => active ? '#323232' : '#AEAEB4'};
	padding-left: 13px;
	position: relative;
	
	&:before {
		content: '$';
		font-weight: 600;
		font-size: 16px;
		line-height: 15px;
		position: absolute;
		top: 0;
		left: 0;
	}
`;

export const Price = styled.div`
	min-width: 40px;
`;

export const SmallItemsWrapper = styled.div`
	font-weight: 600;
	font-size: 16px;
	line-height: 15px;
	text-align: left;
	margin-left: 2px;
`;

export const PerDay = styled.div`
	font-weight: 600;
	font-size: 8px;
	line-height: 10px;
  margin-top: 5px;
`;

export const ConfirmText = styled.p`
  text-align: center;
  color: #545454;
  font-size: 12px;
  font-weight: 400;
	margin-top: 16px;
	
  & > span {
		margin: 0 2px;
    cursor: pointer;
    text-decoration: underline;
  }
`;

export const IconsWrapper = styled(CardIconsList)`
	justify-content: space-between;
	margin: ${({ customMargin }) => customMargin || '24px 0 0'};
	flex-wrap: wrap;
  row-gap: 20px;
  margin-bottom: 24px;
		
  img {
    height: 25px;
    &:not(:last-child) {
      margin-right: 0px;
    }
  }
`;

export const StyledList = styled.ul`
	list-style: none;
	margin: 16px 0 40px;
`;

export const StyledFeatureItem = styled.li`
	position: relative;
	font-size: 16px;
	line-height: 24px;
	color: #323232;
	text-align: left;
	padding-left: 22px;

	&:not(:last-child) {
		margin-bottom: 8px;
	}

	&:before {
		content: '';
		width: 12px;
		height: 12px;
		background-image: url('../assets/plans/checkIcon.svg');
		background-size: cover;
		position: absolute;
		top: 6px;
		left: 0;
	}
`;

const AppBlock = styled.div`
	background: #FFECDB;
	border-radius: 32px;
	padding-top: 24px;
	margin-bottom: 40px;
	overflow: hidden;
`;

const AppScreen = styled.div`
	height: 375px;
	background: url("../assets/plans/app-screen.png") center bottom no-repeat;
	background-size: contain;
	margin-top: 24px;
`;

export const StoreButtons = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
	background-color: #fff;
	padding: 24px 11px;
  position: relative;

  &:after {
    content: '';
    display: block;
    width: 1px;
    height: 75px;
    background-color: #E3E3E3;
    position: absolute;
    top: calc(50% - 37px);
    left: calc(50% - 1px);
  }
`;

export const Store = styled.div`
	padding: 34px 10px 10px;
	background-image: url(${({ img }) => img});
	background-repeat: no-repeat;
	background-position: center top;
	background-size: 65%;
`;

export const StoreRating = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #323232;
`;

export const Stars = styled.div`
  width: 120px;
  height: 20px;
  background: url('../assets/plans/store-stars.png') center / contain no-repeat;
  margin: 4px auto 12px;
`;

export const StoreRatingsCount = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #323232;
  margin-top: 12px;
`;

const QandA = styled.div`
	background: #E9F4FF;
	border-radius: 32px;
	padding: 24px;
	margin-bottom: 40px;
`;

const Accordion = styled.div`
	margin-top: 26px;
`;

const Question = styled.div`
	background-color: #fff;
	border-radius: 16px;
	padding: 16px;
	font-size: 14px;
	line-height: 20px;
	text-align: left;
	position: relative;

	&:not(:last-child) {
		margin-bottom: 8px;
	}

	.q-title {
		display: block;
		font-weight: 600;
		font-size: 16px;
		line-height: 19px;
		transition: margin-bottom 0.2s ease-out;
		padding-right: 20px;
		position: relative;
		
		&-icon {
			position: absolute;
			top: 50%;
			right: 0;
			transform: translateY(-50%);
			transition: transform 0.2s ease-out;
		}
	}
	
	.q-content {
		display: grid;
		grid-template-rows: 0fr;
		transition: grid-template-rows 0.2s ease-out;
		
		&-inner {
			overflow: hidden;
		}
	}
	
	.q-trigger {
		visibility: hidden;
		position: absolute;
		top: 0;
		left: -9999px;

		&:checked {
			& ~ .q-title {
				margin-bottom: 8px;
				
				.q-title-icon {
					transform: translateY(-50%) rotate(180deg);
				}
			}
			
			& ~ .q-content {
				grid-template-rows: 1fr;
			}
		}
	}
`;

export const SubTitle = styled.div`
	font-size: 16px;
	text-align: center;
	color: #0F0E0E;
  font-weight: 400;	
	margin-top: 16px;

	span {
		font-weight: 600;		
	}
`;

export const ReviewBlock = styled.div`
	margin: 24px 0 60px;
`;

export const MoneyBackWrapper = styled.div`
	max-width: 330px;
	width: 100%;
	border-radius: 32px;
	border: 3px solid #F58A3C;
	background-color: #fff;
	padding: 40px 19px 30px;
	margin-bottom: 40px;
	position: relative;
	
	&:before,
	&:after {
		content: '';
		display: block;
		width: 60px;
		height: 60px;
		border-radius: 50%;
		position: absolute;
		top: -36px;
		left: 50%;
	}
	
	&:before {
		border: inherit;
		border-top-color: transparent;
		border-right-color: transparent;
		transform: translateX(-50%) rotate(-45deg);
		background-color: #ffffff;
	}
	
	&:after {
		transform: translateX(-50%);
		background: url('../assets/plans/moneyBack.svg') center no-repeat;
	}
`;

export const MoneyBackTitle = styled.div`
	font-weight: 700;
	font-size: 24px;
	line-height: 32px;
	color: #F58A3C;
	text-align: center;
`;

export const MoneyBackDescription = styled.div`
	font-size: 14px;
	line-height: 24px;
	color: #909090;
  font-weight: 400;
	margin-top: 12px;
	text-align: left;
	
	& > span {
		text-decoration: underline;
		cursor: pointer;
	}
`;

const HeadwayPlans = ({ onContinueClick, handleSetPlan }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [planDetails, setPlanDetails] = useState({});
	const plan = useSelector(selectPlan);
	const couponDetails = useSelector(selectPlanCoupon);
	const showCheckout = useSelector(selectShowCheckout);
	const landingType = useSelector(selectLandingType);
	const locations = useSelector(selectLocation);
	const frequency = useSelector(selectFrequency);
  const isTrial = ['trial'].includes(landingType);

	useEffect(() => {
		if (plan) {
			setPlanDetails(PLANS[plan]);
		}
	}, [plan]);

	useEffect(() => {
		if (!showCheckout) {
			handleSetPlan(currentPlans[0]?.id, currentPlans[0]?.coupon);
			dispatch(setDefaultPlanId(currentPlans[0]?.id));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, showCheckout]);

	const EXTENDED_CARD_BRANDS = useMemo(() => {
		return [
			{ name: 'visa' },
			{ name: 'mastercard' },
			{ name: 'paypal' },
			{ name: 'amex' },
			{ name: 'discover' },
			{ name: 'apple' },
			{ name: 'google' },
		];
	}, []);

  const defaultPaywall = useMemo(() => {
    return [
      {
        id: "fm-monthly-39.99",
        title: `1 ${t('month')}`,
        price: '1',
        cents: '33',
        oldPrice: '$1.85',
        week: `${t('per_day')}`,
        bestValue: true,
      },
      {
        id: "fm-3month-59.99",
        title: `3 ${t('months')}`,
        price: '0',
        cents: '66',
        oldPrice: '$0,88',
        week: `${t('per_day')}`,
      },
      {
        id: "fm-6month-71.76",
        title: `6 ${t('months')}`,
        price: '0',
        cents: '38',
        oldPrice: '$0,50',
        week: `${t('per_day')}`,
      },
    ];
  }, [t]);

	const trialPaywall = useMemo(() => [
		{
			id: 'fm-monthly-intro7d-6.99-39.99',
			title: `1 ${t('week')}`,
			price: '0',
			cents: '99',
			oldPrice: '$1.42',
      fullPrice: '$6.99',
			oldFullPrice: '$9.99',
			week: `${t('per_day')}`,
		},
		{
			id: 'fm-monthly-39.99',
			title: `1 ${t('month')}`,
			price: '0',
			cents: '64',
			oldPrice: '$0.96',
      fullPrice: '$19.99',
			oldFullPrice: '$29.99',
			week: t('per_day'),
      coupon: 'discount_20_usd_off_onetime'
		},
		{
			id: 'fm-3month-59.99',
			title: `3 ${t('months')}`,
			price: '0',
			cents: '32',
			oldPrice: '$0.43',
      fullPrice: '$29.99',
			oldFullPrice: '$39.99',
			week: t('per_day'),
      coupon: 'discount_30_usd_off_onetime'
		},
	], [t]);


	const OFFERS = useMemo(() => {
		return {
      up_v2: defaultPaywall,
			trial: trialPaywall,
		}
	}, [trialPaywall, defaultPaywall]);

	const currentPlans = useMemo(() => OFFERS[landingType], [landingType, OFFERS]);

	/*  Lists  */
	const REVIEWS = useMemo(() => {
    return [
      {
        id: 1,
        market: "IOS",
        reviewer: "Kevin Peters",
        reviewerSource: '../assets/reviewer1.png',
        text: t("review_1"),
      },
      {
        id: 2,
        market: "Play",
        reviewer: "John Lee",
        reviewerSource: '../assets/reviewer2.png',
        text: t("review_2"),
      },
      {
        id: 3,
        market: "Play",
        reviewer: "Megan Smith",
        reviewerSource: '../assets/reviewer3.png',
        text: t("review_3"),
      },
    ];
  }, [t]);

	const FEATURES = useMemo(() => [
		t('know_when_family_come_or_go'),
		t('realtime_location_sharing'),
		t('find_loved_ones'),
		t('be_notified_in_case'),
		t('get_notified_when_the_land'),
	], [t]);

	const CHOICES =  useMemo(() => [
		{
			type: 'locations',
			icon: <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="10" fill="#FFECDB"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M17.25 12C17.25 10.4812 18.4812 9.25 20 9.25C21.5188 9.25 22.75 10.4812 22.75 12C22.75 13.5188 21.5188 14.75 20 14.75C18.4812 14.75 17.25 13.5188 17.25 12Z" fill="#F58A3C"/>
      <path d="M16.2231 19.5743L14.0478 18.8492C13.4221 18.6407 13 18.0551 13 17.3955C13 16.4199 13.8995 15.6927 14.8535 15.8971L16.751 16.3038C16.8565 16.3264 16.9093 16.3377 16.9616 16.3485C18.966 16.7631 21.034 16.7631 23.0384 16.3485C23.0907 16.3377 23.1435 16.3264 23.249 16.3038L25.1465 15.8971C26.1005 15.6927 27 16.4199 27 17.3955C27 18.0551 26.5779 18.6407 25.9522 18.8492L23.7769 19.5743C23.514 19.662 23.3826 19.7058 23.2837 19.7668C22.9471 19.9746 22.7646 20.3613 22.8182 20.7532C22.834 20.8683 22.8837 20.9976 22.9832 21.2562L24.23 24.4979C24.6011 25.4629 23.8888 26.5 22.8549 26.5C22.3263 26.5 21.8381 26.2167 21.5758 25.7577L20 23L18.4242 25.7577C18.1619 26.2167 17.6737 26.5 17.1451 26.5C16.1112 26.5 15.3989 25.4629 15.77 24.4979L17.0168 21.2562C17.1163 20.9976 17.166 20.8683 17.1818 20.7532C17.2354 20.3613 17.0529 19.9746 16.7163 19.7668C16.6174 19.7058 16.486 19.662 16.2231 19.5743Z" fill="#F58A3C"/>
      <path d="M20 30.0001C25.5228 30.0001 30 27.9854 30 25.5001C30 23.7782 27.8509 22.2822 24.694 21.5256L25.63 23.9593C26.379 25.9067 24.9415 27.9998 22.8549 27.9998C21.788 27.9998 20.8028 27.4281 20.2735 26.5017L20 26.0232L19.7265 26.5017C19.1972 27.4281 18.212 27.9998 17.1451 27.9998C15.0585 27.9998 13.621 25.9067 14.37 23.9593L15.306 21.5256C12.1491 22.2822 10 23.7782 10 25.5001C10 27.9854 14.4772 30.0001 20 30.0001Z" fill="#F58A3C"/>
      </svg>,
			items: locations,
			text: t('locations_you_want'),
		},
		{
			type: 'frequency',
			icon: <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
      <rect width="40" height="40" rx="10" fill="#FFECDB"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M17.0996 10.3975C17.2487 10.784 17.0564 11.2181 16.67 11.3673C16.5286 11.4219 16.3888 11.4799 16.2508 11.5411C15.8722 11.7092 15.4291 11.5386 15.261 11.16C15.0929 10.7814 15.2636 10.3382 15.6422 10.1702C15.8027 10.0989 15.9653 10.0315 16.1298 9.96795C16.5162 9.81878 16.9504 10.0111 17.0996 10.3975ZM13.6477 12.2403C13.9334 12.5402 13.9218 13.0149 13.6218 13.3006C13.5122 13.4051 13.4051 13.5122 13.3006 13.6218C13.0149 13.9218 12.5402 13.9334 12.2403 13.6477C11.9403 13.362 11.9287 12.8873 12.2144 12.5874C12.3357 12.46 12.46 12.3357 12.5874 12.2144C12.8873 11.9287 13.362 11.9403 13.6477 12.2403ZM11.16 15.261C11.5386 15.4291 11.7092 15.8722 11.5411 16.2508C11.4799 16.3888 11.4219 16.5286 11.3673 16.67C11.2181 17.0564 10.784 17.2487 10.3975 17.0996C10.0111 16.9504 9.81878 16.5162 9.96795 16.1298C10.0315 15.9653 10.0989 15.8027 10.1702 15.6422C10.3382 15.2636 10.7814 15.0929 11.16 15.261ZM10.0211 19.004C10.4352 19.0141 10.7628 19.3579 10.7528 19.7719C10.7509 19.8477 10.75 19.9237 10.75 20C10.75 20.0763 10.7509 20.1523 10.7528 20.2281C10.7628 20.6421 10.4352 20.9859 10.0211 20.996C9.60699 21.006 9.26319 20.6784 9.25319 20.2643C9.25107 20.1764 9.25 20.0883 9.25 20C9.25 19.9117 9.25107 19.8236 9.25319 19.7357C9.26319 19.3216 9.60699 18.994 10.0211 19.004ZM29.6025 22.9004C29.9889 23.0496 30.1812 23.4838 30.032 23.8702C29.9685 24.0347 29.9011 24.1973 29.8298 24.3578C29.6618 24.7364 29.2186 24.9071 28.84 24.739C28.4614 24.5709 28.2908 24.1278 28.4589 23.7492C28.5201 23.6112 28.5781 23.4714 28.6327 23.33C28.7819 22.9436 29.216 22.7513 29.6025 22.9004ZM10.3975 22.9004C10.784 22.7513 11.2181 22.9436 11.3673 23.33C11.4219 23.4714 11.4799 23.6112 11.5411 23.7492C11.7092 24.1278 11.5386 24.5709 11.16 24.739C10.7814 24.9071 10.3382 24.7364 10.1702 24.3578C10.0989 24.1973 10.0315 24.0347 9.96795 23.8702C9.81878 23.4838 10.0111 23.0496 10.3975 22.9004ZM27.7597 26.3523C28.0597 26.638 28.0713 27.1127 27.7856 27.4126C27.6643 27.54 27.54 27.6643 27.4126 27.7856C27.1127 28.0713 26.638 28.0597 26.3523 27.7597C26.0666 27.4598 26.0782 26.9851 26.3782 26.6994C26.4878 26.5949 26.5949 26.4878 26.6994 26.3782C26.9851 26.0782 27.4598 26.0666 27.7597 26.3523ZM12.2403 26.3523C12.5402 26.0666 13.0149 26.0782 13.3006 26.3782C13.4051 26.4878 13.5122 26.5949 13.6218 26.6994C13.9218 26.9851 13.9334 27.4598 13.6477 27.7597C13.362 28.0597 12.8873 28.0713 12.5874 27.7856C12.46 27.6643 12.3357 27.54 12.2144 27.4126C11.9287 27.1127 11.9403 26.638 12.2403 26.3523ZM15.261 28.84C15.4291 28.4614 15.8722 28.2908 16.2508 28.4589C16.3888 28.5201 16.5286 28.5781 16.67 28.6327C17.0564 28.7819 17.2487 29.216 17.0996 29.6025C16.9504 29.9889 16.5162 30.1812 16.1298 30.032C15.9653 29.9685 15.8027 29.9011 15.6422 29.8298C15.2636 29.6618 15.0929 29.2186 15.261 28.84ZM24.739 28.84C24.9071 29.2186 24.7364 29.6618 24.3578 29.8298C24.1973 29.9011 24.0347 29.9685 23.8702 30.032C23.4838 30.1812 23.0496 29.9889 22.9004 29.6025C22.7513 29.216 22.9436 28.7819 23.33 28.6327C23.4714 28.5781 23.6112 28.5201 23.7492 28.4589C24.1278 28.2908 24.5709 28.4614 24.739 28.84ZM19.004 29.9789C19.0141 29.5648 19.3579 29.2372 19.7719 29.2472C19.8477 29.2491 19.9237 29.25 20 29.25C20.0763 29.25 20.1523 29.2491 20.2281 29.2472C20.6421 29.2372 20.9859 29.5648 20.996 29.9789C21.006 30.393 20.6784 30.7368 20.2643 30.7468C20.1764 30.7489 20.0883 30.75 20 30.75C19.9117 30.75 19.8236 30.7489 19.7357 30.7468C19.3216 30.7368 18.994 30.393 19.004 29.9789Z" fill="#F58A3C"/>
      <path fillRule="evenodd" clipRule="evenodd" d="M19.25 10C19.25 9.58579 19.5858 9.25 20 9.25C25.9371 9.25 30.75 14.0629 30.75 20C30.75 20.4142 30.4142 20.75 30 20.75C29.5858 20.75 29.25 20.4142 29.25 20C29.25 14.8914 25.1086 10.75 20 10.75C19.5858 10.75 19.25 10.4142 19.25 10ZM20 16.25C20.4142 16.25 20.75 16.5858 20.75 17V20.25H24C24.4142 20.25 24.75 20.5858 24.75 21C24.75 21.4142 24.4142 21.75 24 21.75H20C19.5858 21.75 19.25 21.4142 19.25 21V17C19.25 16.5858 19.5858 16.25 20 16.25Z" fill="#F58A3C"/>
      </svg>,
			items: frequency,
			text: t('frequency'),
		},
	], [t, locations, frequency]);

	const QUESTIONS = useMemo(() => [
		{
			title: t('question_1'),
			content: t('answer_1'),
		},
		{
			title: t('question_2'),
			content: t('answer_2'),
		},
		{
			title: t('question_3'),
			content: t('answer_3'),
		},
	], [t]);

	const STORES = [
		{
			name: 'App Store',
			event: 'appleStoreTapped',
			icon: '../assets/plans/app-store.svg',
			grade: 4.6,
			rating: '36.9K',
		},
		{
			name: 'Google Play',
			event: 'googlePlayTapped',
			icon: '../assets/plans/google-play.svg',
			grade: 4.5,
			rating: '412K',
		},
	];
	/*  End Lists  */

	/*  Renders  */
	const renderPlans = (planInfo, index) => {
		const { id, title, price, cents, oldPrice, coupon, fullPrice, oldFullPrice } = planInfo;
		const isActive = plan === id;
		return (
			<PlanLi
				active={isActive}
				onClick={() => handleSetPlan(id, coupon)}
				id={'plan-button-' + (index + 1)}
				key={id}
			>
				<StyledPeriod>
					<PlanCheck active={isActive} />
					<PlanTitle>
						<div>{title}</div>
            {isTrial && (
							<StyledFullPrice><strike>{oldFullPrice}</strike> {fullPrice}</StyledFullPrice>
						)}
					</PlanTitle>
				</StyledPeriod>
				<StyledPriceWrapper isTrial={isTrial}>
					<OldPrice>{oldPrice}</OldPrice>
					<PriceWrapper active={isActive}>
						<Price>{price}</Price>
						<SmallItemsWrapper>
							<span>{cents}</span>
							<PerDay>{t('per_day')}</PerDay>
						</SmallItemsWrapper>
					</PriceWrapper>
				</StyledPriceWrapper>
			</PlanLi>
		);
	};

	const renderReview = (r, i) => {
    return (
      <ReviewItem key={i}>
        <ReviewHead>
          <UserIcon imgUserPath={'../assets/app-icon.svg'}/>
          <Label>{r.market}</Label>
          <Rate imgStarPath={'../assets/stars.png'}/>
        </ReviewHead>
        <ReviewBody>
          <ReviewerPhoto src={r.reviewerSource} alt={r.reviewer} />
          <ReviewerName>{r.reviewer}</ReviewerName>
        </ReviewBody>
        <ReviewText>{r.text}</ReviewText>
      </ReviewItem>
    );
  };

	const renderFeature = (i, index) => <StyledFeatureItem key={index}>{i}</StyledFeatureItem>;

	const renderChoice = ({ type, icon, items, text }) => {
		const isFrequency = type === 'frequency';
		return (
			<ChoicesBlockItem key={type}>
				<ChoicesIcon>{icon}</ChoicesIcon>
				<ChoicesText isFrequency={isFrequency}>
					{text}
					<ChoicesItems>{isFrequency ? items : items.join(', ')}</ChoicesItems>
				</ChoicesText>
			</ChoicesBlockItem>
		);
	};

	const renderQuestion = (q, index) => (
		<Question key={index}>
			<input type={'checkbox'} name={'accordion'} className={'q-trigger'} id={`q-trigger-${index}`} />
			<label className={'q-title'} htmlFor={`q-trigger-${index}`}>
				{q.title}
				<svg className={'q-title-icon'} width="24" height="24" viewBox="0 0 24 24" fill="none">
					<path fillRule="evenodd" clipRule="evenodd" d="M5.94451 9.20954C6.16016 8.95794 6.53894 8.9288 6.79053 9.14446L12.0001 13.6098L17.2096 9.14446C17.4612 8.9288 17.84 8.95794 18.0556 9.20954C18.2713 9.46113 18.2421 9.83991 17.9905 10.0556L12.3905 14.8556C12.1658 15.0482 11.8343 15.0482 11.6096 14.8556L6.00958 10.0556C5.75799 9.83991 5.72885 9.46113 5.94451 9.20954Z" fill="#7E7E7E"/>
				</svg>
			</label>
			<div className={'q-content'}>
				<div className={'q-content-inner'}>{q.content}</div>
			</div>
		</Question>
	);

	const renderIcon = (item) => (
		<img
			key={item.name}
			src={`../assets/checkout/trial/${item.name}.png`}
			alt={item.name}
			width={item.width}
			height={item.height}
		/>
	);

	const renderStore = (i, index) => (
		<Store key={index} img={i.icon}>
			<StoreRating>{i.grade}</StoreRating>
			<Stars />
			<StoreRatingsCount>
				{i.rating} {t('ratings')}
			</StoreRatingsCount>
		</Store>
	);
	/*  End Renders  */

	const onLinkClick = ({ target: { id } }) => {
		const termsLink = 'https://familo.net/en/assets/Terms_and_Conditions_Familonet_info.pdf';
		const links = {
			privacy: 'https://terms.familo.net/privacy-info',
			terms: termsLink,
			refund: termsLink,
			'terms-of-use': termsLink,
			'fo-link': 'https://familo.net'
		};

		if (id && links[id]) window.open(links[id]);
	};

	/*  Components  */
	const disclaimer = (
		<ConfirmText
			dangerouslySetInnerHTML={{ __html: t('paywall_disclaimer') }}
			onClick={(e) => onLinkClick(e)}
		/>
	);

	const TrialDisclaimer = () => {
		const { price, periodPrice, duration } = planDetails;
		const { couponPrice } = couponDetails;
		const finalPrice = +(periodPrice - couponPrice).toFixed(2);

		return (
			<ConfirmText
					dangerouslySetInnerHTML={{
						__html: t(
							'trial_disclaimer',
							{
								price,
								periodPrice: finalPrice,
								duration,
							}
						)
					}}
					onClick={(e) => onLinkClick(e)}
				/>
		)
	};

	const continueButton = (customText, customStyles, id) => (
		<UIButton
			customText={t(customText)}
			onClick={onContinueClick}
			bottomPosition={'16px'}
			displayStickyBtn={false}
			position={'fixed'}
			customBeforeHeight={false}
			customStyles={customStyles}
			customId={`paywall-continue-button-${id}`}
		/>
	);

	const PlansFragment = ({ children, id }) => (
		<>
			<Title>{t('choose_your_plan')}</Title>
			{children}
			<PlanList>{currentPlans.map(renderPlans)}</PlanList>
			{continueButton('continue', 'margin-top: 5px', id)}
			<IconsWrapper>{EXTENDED_CARD_BRANDS.map(renderIcon)}</IconsWrapper>
      {isTrial ? <TrialDisclaimer /> : disclaimer}
		</>
	);
	/*  End Components  */

	return (
		<Wrapper>
      <StyledLogoWrapper>
        <Logo invert={true} />
      </StyledLogoWrapper>
			<TimerBlock>
				<div>
					<TimerBlockDiscount>{t('discount_text')}</TimerBlockDiscount>
					<TimerSimple />
				</div>
				{continueButton('continue', 'width:105px;min-height:40px;height:40px;margin:0; padding:0', 1)}
			</TimerBlock>
			<Title customMargin={'0 0 16px 0'}>{t('make_sure_title')}</Title>
			<Description>{t('get_full_history')}</Description>
			<StyledImage />
			<PlansFragment id={2}>
				<ChoicesBlock>{(locations || frequency) && CHOICES.map(renderChoice)}</ChoicesBlock>
			</PlansFragment>
			<Title customMargin={'40px 0 0'}>{t('what_you_get')}</Title>
			<StyledList>{FEATURES.map(renderFeature)}</StyledList>
			<AppBlock>
				<Title>{t('get_full_location_history')}</Title>
				<AppScreen />
				<StoreButtons>{STORES.map(renderStore)}</StoreButtons>
			</AppBlock>
			<QandA>
				<Title>{t('we_are_ready_to_answer')}</Title>
				<Accordion>{QUESTIONS.map(renderQuestion)}</Accordion>
			</QandA>
			<PlansFragment id={3}>
				<SubTitle dangerouslySetInnerHTML={{ __html: t('exclusive_sign_up') }} />
			</PlansFragment>
			<Title customMargin={'40px 0 0'}>{t('people_love')}</Title>
			<ReviewBlock>{REVIEWS.map(renderReview)}</ReviewBlock>
			<MoneyBackWrapper>
				<MoneyBackTitle>{t('money_back_guarantee')}</MoneyBackTitle>
				<MoneyBackDescription
					dangerouslySetInnerHTML={{ __html: t('guarantee_text') }}
					onClick={(e) => onLinkClick(e)}
				/>
			</MoneyBackWrapper>
			<Title dangerouslySetInnerHTML={{ __html: t('safe_checkout') }} />
			<IconsWrapper customMargin={'24px 0'}>{EXTENDED_CARD_BRANDS.map(renderIcon)}</IconsWrapper>
			{continueButton('continue', 'margin: 0 0 16px 0', 4)}
			{disclaimer}
		</Wrapper>
	);
};

export default HeadwayPlans;
